import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  makeStyles,
  Typography,
  Paper,
  Icon,
  Box,
  CircularProgress,
  Select,
  MenuItem,
} from "@material-ui/core";
import Draggable from "react-draggable";
import { toast } from "react-toastify";

import { commonIcon, CONTACT_SUPPORT_ICON } from "./ContactSupportButton";
import { isNil, size } from "lodash";
import { sendAskSupportHelp } from "./api/supportApi";
import { useRequestHeaders } from "../../../../core/hooks/useRequestHeaders";
import { AuthContext } from "../../../../core/providers/AuthContext";
import { TOAST_CONTAINER_LAYOUT } from "../../../../util/Constants";
import { useTranslation } from "react-i18next";
import { COUNTRIES, SOLUTIONS } from "./contact-support-constants";

const TITLE_ID = "contact-support-dialog-title";

const INITIAL_FORM_DATA = {
  subject: "",
  message: "",
  solution: "",
  country: "",
};

const useStyles = makeStyles((theme) => ({
  dialogTitle: {
    cursor: "move",
    display: "flex",
    padding: "16px 28px 0px 28px",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dialogTitleStartBox: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    justifyContent: "center",
  },
  dialogTitleText: {
    fontSize: "1.75rem",
  },
  dialogContent: {
    padding: "0px 24px !important",
  },
  dialogContentDescBox: {
    marginBottom: 12,
  },
  dialogContentDescText: {
    fontSize: "1rem",
  },
  dialogContentErrorText: {
    fontSize: "0.8rem",
    fontStyle: "italic",
    fontWeight: "bold"
  },
  sopLogoContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  sopLogo: {
    maxWidth: 150,
    marginInline: "auto",
  },
  dialogActions: {
    display: "flex",
    padding: "16px 24px",
    alignItems: "center",
  },
  fieldLabel: {
    fontSize: "1rem",
    fontWeight: 500,
  },
  fieldLabelChilds: {
    marginTop: 6,
  },
  selects: {
    padding: 12,
  },
  attachLabelBox: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
    marginTop: 10,
  },
  attachBtn: {
    height: 38,
    marginTop: 4,
  },
  attachBtnClear: {
    padding: 4,
    border: `1px solid ${theme.palette.semaphore.red}`,
    textTransform: "none",
    color: theme.palette.semaphore.red,
  },
  inlineRow: {
    display: "flex",
    flexDirection: "row",
    gap: "12px",
  },
  columnBox: {
    display: "flex",
    alignItems: "start",
    flexDirection: "column",
    justifyContent: "start",
    width: "100%",
  },
}));

const commonEmoji = {
  fontSize: "1.75rem",
  marginRight: 6,
  marginLeft: 6,
};

function PaperComponent(props) {
  return (
    <Draggable
      handle={`#${TITLE_ID}`}
      cancel={'[class*="MuiDialogContent-root"]'}
    >
      <Paper {...props} />
    </Draggable>
  );
}

export default function ContactSupportDialog({ open, onClose }) {
  const classes = useStyles();
  const { t } = useTranslation();
  const inputRef = useRef();
  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const [files, setFiles] = useState([]);

  const [loading, setLoading] = useState(false);
  const requestHeaders = useRequestHeaders();
  const { logout } = useContext(AuthContext);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const toastOptions = {
      containerId: TOAST_CONTAINER_LAYOUT,
      autoClose: 10000,
      toastId: "handleSubmit",
    };

    let ok = false;
    try {
      const res = await sendAskSupportHelp({
        headers: requestHeaders,
        logout,
        subject: formData.subject,
        message: formData.message,
        solution: formData.solution,
        country: formData.country,
        files: files,
      });

      if (res.ok === true) {
        ok = true;
        toast.success(t("SUPPORT.DIALOG_ACTION_OK"), toastOptions);
      } else {
        ok = false;
        toast.error(
          res?.error || t("SUPPORT.DIALOG_ACTION_ERROR"),
          toastOptions
        );
      }
    } catch (error) {
      toast.error(
        error?.message || t("SUPPORT.DIALOG_ACTION_ERROR"),
        toastOptions
      );
    } finally {
      if (ok) {
        setTimeout(() => {
          onClose();
          setLoading(false);
        }, 500);
      } else {
        setTimeout(() => {
          setLoading(false);
        }, 500);
      }
    }
  };

  const sendEnabled = useMemo(() => {
    return (
      !isNil(formData?.subject) &&
      size(formData?.subject) > 0 &&
      !isNil(formData?.message) &&
      size(formData?.message) > 0 &&
      !isNil(formData?.solution) &&
      size(formData?.solution) > 0 &&
      !isNil(formData?.country) &&
      size(formData?.country) > 0
    );
  }, [formData]);

  const handleAddFiles = (e) => {
    const newFiles = Array.from(e.target.files);
    setFiles((prevFiles) => [...prevFiles, ...newFiles]);
  };

  function openFileChooserDialog() {
    if (inputRef && inputRef.current) {
      inputRef.current.click();
    }
  }

  useEffect(() => {
    if (open === true) {
      setFormData(INITIAL_FORM_DATA);
      setFiles([]);
    }
  }, [open]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      fullWidth
      PaperComponent={PaperComponent}
      aria-labelledby={TITLE_ID}
    >
      <DialogTitle
        className={classes.dialogTitle}
        disableTypography
        id={TITLE_ID}
      >
        <Box className={classes.dialogTitleStartBox}>
          <Icon
            className={CONTACT_SUPPORT_ICON}
            style={{ ...commonIcon, fontSize: "1.75rem" }}
          />
          <Typography variant="h2" className={classes.dialogTitleText}>
            {t("SUPPORT.DIALOG_TITLE")}
          </Typography>
        </Box>

        <Box className={classes.sopLogoContainer}>
          <img
            alt={`dsuite-logo`}
            src={`${process.env.PUBLIC_URL}/assets/img/DSUITE_logo_new.webp`}
            className={classes.sopLogo}
          />
        </Box>
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent className={classes.dialogContent}>
          <Box className={classes.dialogContentDescBox}>
            <Typography
              color="error"
              className={classes.dialogContentErrorText}
            >
              {t("SUPPORT.DIALOG_DISCLAIMER")}
            </Typography>

            <Typography className={classes.dialogContentDescText}>
              <span role="img" aria-labelledby="writing" style={commonEmoji}>
                👨‍💻
              </span>
              {t("SUPPORT.DIALOG_SUBTITLE")}
            </Typography>
          </Box>

          <Typography className={classes.fieldLabel}>
            {t("SUPPORT.DIALOG_FORM_FIELD_SUBJECT")}
          </Typography>
          <TextField
            autoFocus
            margin="dense"
            name="subject"
            type="text"
            fullWidth
            variant="outlined"
            value={formData.subject}
            onChange={handleChange}
            required
          />
          <Typography
            className={`${classes.fieldLabel} ${classes.fieldLabelChilds}`}
          >
            {t("SUPPORT.DIALOG_FORM_FIELD_MESSAGE")}
          </Typography>
          <TextField
            margin="dense"
            name="message"
            multiline
            minRows={5}
            fullWidth
            variant="outlined"
            value={formData.message}
            onChange={handleChange}
            required
          />
          <Box className={classes.inlineRow}>
            <Box className={classes.columnBox}>
              <Typography
                className={`${classes.fieldLabel} ${classes.fieldLabelChilds}`}
              >
                {t("SUPPORT.DIALOG_FORM_FIELD_SOLUTIONS")}
              </Typography>
              <Select
                fullWidth
                variant="outlined"
                name="solution"
                value={formData.solution}
                onChange={handleChange}
                classes={{ root: classes.selects }}
              >
                {SOLUTIONS.map((solution) => (
                  <MenuItem key={solution} value={solution}>
                    {solution}
                  </MenuItem>
                ))}
              </Select>
            </Box>
            <Box className={classes.columnBox}>
              <Typography
                className={`${classes.fieldLabel} ${classes.fieldLabelChilds}`}
              >
                {t("SUPPORT.DIALOG_FORM_FIELD_COUNTRY")}
              </Typography>
              <Select
                fullWidth
                variant="outlined"
                name="country"
                value={formData.country}
                onChange={handleChange}
                classes={{ root: classes.selects }}
              >
                {COUNTRIES.map((country) => (
                  <MenuItem key={country} value={country}>
                    {country}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          </Box>

          <Box className={classes.attachLabelBox}>
            <Typography className={`${classes.fieldLabel}`}>
              {t("SUPPORT.DIALOG_FORM_FIELD_ATTACHMENTS")}
            </Typography>
            {size(files) > 0 && (
              <Button
                variant="outlined"
                className={classes.attachBtnClear}
                endIcon={<Icon className="fas fa-trash" fontSize="small" />}
                onClick={() => setFiles([])}
              >
                {t("FORM_TABLE_FILE_CH_DIALOG_BUTTON_DELETE")}
              </Button>
            )}
          </Box>

          <Button
            fullWidth
            variant="outlined"
            color="default"
            className={classes.attachBtn}
            startIcon={<Icon className="fas fa-paperclip" />}
            onClick={() => openFileChooserDialog()}
          >
            {`${t("FORM_TABLE_FILE_CH_DIALOG_BUTTON_ADD")}${
              size(files) > 0 ? ` (${size(files)})` : ``
            }`}
          </Button>

          <input
            ref={inputRef}
            id="contact_support_files_input"
            type="file"
            style={{ display: "none" }}
            multiple={true}
            onChange={handleAddFiles}
            value={""}
          />
        </DialogContent>
        <DialogActions className={classes.dialogActions}>
          <Button
            onClick={onClose}
            variant="outlined"
            startIcon={
              <Icon
                className="fas fa-trash-alt"
                style={commonIcon}
                fontSize="medium"
              />
            }
            disabled={loading}
          >
            {t("SUPPORT.DIALOG_ACTIONS_DISCARD")}
          </Button>
          <Button
            type="submit"
            variant="outlined"
            color="primary"
            endIcon={
              loading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <Icon
                  className="fas fa-paper-plane"
                  style={commonIcon}
                  fontSize="medium"
                />
              )
            }
            disabled={!sendEnabled || loading}
          >
            {loading
              ? t("SUPPORT.DIALOG_ACTIONS_SENDING")
              : t("SUPPORT.DIALOG_ACTIONS_SEND")}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
